import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "../../sanity/client";
import NavigationBar from "../../Components/HighLevelComponents/NavigationBar/NavigationBar";

interface Tool {
  _id: string;
  title: string;
  description: string;
  slug: {
    current: string;
  };
  hero: {
    image: {
      asset: {
        url: string;
      };
    };
  };
}

const ToolsLandingPage: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const query = `*[_type == "tool"] {
          _id,
          title,
          description,
          slug,
          hero {
            image {
              asset-> {
                url
              }
            }
          }
        }`;

        type FetchFunction = (query: string) => Promise<Tool[]>;
        const result = await (client.fetch as FetchFunction)(query);
        setTools(result);
      } catch (err) {
        setError("Error loading tools");
        console.error("Error fetching tools:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-center mb-12">Our Tools</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tools.map((tool) => (
            <Link
              key={tool._id}
              to={`/landing-tools/${tool.slug.current}`}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              {tool.hero?.image && (
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={tool.hero.image.asset.url}
                    alt={tool.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-2">{tool.title}</h2>
                <p className="text-gray-600">{tool.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ToolsLandingPage;
